import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import { Checkbox } from "../../../components/ui/checkbox";
import {
  Plus,
  Search,
  Send,
  Users,
  FileText,
  Calendar,
  Eye,
  Edit,
  Trash2,
  Copy,
  Clock,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { toast } from "sonner";
import { getUsers } from "../../../api/userApi";
import NotificationApiService from "../../../api/notificationApi";
import CreateGroupModal from "./CreateGroupModal";
import CreateTemplateModal from "./CreateTemplateModal";
import CreateScheduledNotificationModal from "./CreateScheduledNotificationModal";
import SendNotificationModal from "./SendNotificationModal";
import GroupDetailsModal from "./GroupDetailsModal";

export default function ManageNotifications() {
  // State for data
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [scheduledNotifications, setScheduledNotifications] = useState([]);
  const [sentNotifications, setSentNotifications] = useState([]);

  // Loading states
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isLoadingScheduled, setIsLoadingScheduled] = useState(false);
  const [isLoadingSent, setIsLoadingSent] = useState(false);

  // Search states
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [groupSearchQuery, setGroupSearchQuery] = useState("");
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");
  const [scheduledSearchQuery, setScheduledSearchQuery] = useState("");
  const [sentSearchQuery, setSentSearchQuery] = useState("");

  // Selection states
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectedScheduled, setSelectedScheduled] = useState([]);

  // Modal states
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [showCreateScheduled, setShowCreateScheduled] = useState(false);
  const [showSendNotification, setShowSendNotification] = useState(false);
  const [showGroupDetails, setShowGroupDetails] = useState(false);
  const [selectedGroupForDetails, setSelectedGroupForDetails] = useState(null);

  // Stats
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalGroups: 0,
    totalTemplates: 0,
    totalScheduled: 0,
    totalSent: 0,
  });

  // Pagination for scheduled notifications
  const [scheduledPagination, setScheduledPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  // Pagination for sent notifications
  const [sentPagination, setSentPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await getUsers();
      if (response.success) {
        setUsers(response.data || []);
        setStats((prev) => ({
          ...prev,
          totalUsers: response.data?.length || 0,
        }));
      } else {
        toast.error("Failed to fetch users");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users");
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const fetchGroups = async () => {
    setIsLoadingGroups(true);
    try {
      const response = await NotificationApiService.getGroups();
      if (response.success) {
        setGroups(response.data || []);
        setStats((prev) => ({
          ...prev,
          totalGroups: response.data?.length || 0,
        }));
      } else {
        toast.error("Failed to fetch groups");
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
      toast.error("Failed to fetch groups");
    } finally {
      setIsLoadingGroups(false);
    }
  };

  const fetchTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      const response = await NotificationApiService.getTemplates();
      if (response.success) {
        setTemplates(response.data || []);
        setStats((prev) => ({
          ...prev,
          totalTemplates: response.data?.length || 0,
        }));
      } else {
        toast.error("Failed to fetch templates");
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Failed to fetch templates");
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const fetchScheduledNotifications = async () => {
    setIsLoadingScheduled(true);
    try {
      const response = await NotificationApiService.listScheduledNotifications(
        undefined,
        scheduledPagination.page,
        scheduledPagination.limit
      );
      if (response.success) {
        const sortedNotifications = response.notifications.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.scheduledFor || 0);
          const dateB = new Date(b.createdAt || b.scheduledFor || 0);
          return dateB.getTime() - dateA.getTime();
        });

        setScheduledNotifications(sortedNotifications);
        setScheduledPagination(response.pagination);
        setStats((prev) => ({
          ...prev,
          totalScheduled: response.pagination?.total || 0,
        }));
      } else {
        toast.error("Failed to fetch scheduled notifications");
      }
    } catch (error) {
      console.error("Error fetching scheduled notifications:", error);
      toast.error("Failed to fetch scheduled notifications");
    } finally {
      setIsLoadingScheduled(false);
    }
  };

  const fetchSentNotifications = async () => {
    setIsLoadingSent(true);
    try {
      const response = await NotificationApiService.getSentNotifications(
        sentPagination.page,
        sentPagination.limit
      );
      if (response.success) {
        setSentNotifications(response.data?.notifications || []);
        setSentPagination(response.data?.pagination || {});
        setStats((prev) => ({
          ...prev,
          totalSent: response.data?.pagination?.total || 0,
        }));
      } else {
        toast.error("Failed to fetch sent notifications");
      }
    } catch (error) {
      console.error("Error fetching sent notifications:", error);
      toast.error("Failed to fetch sent notifications");
    } finally {
      setIsLoadingSent(false);
    }
  };

  const refreshAllData = async () => {
    setScheduledPagination((prev) => ({ ...prev, page: 1 }));
    setSentPagination((prev) => ({ ...prev, page: 1 }));

    await Promise.all([
      fetchUsers(),
      fetchGroups(),
      fetchTemplates(),
      fetchScheduledNotifications(),
      fetchSentNotifications(),
    ]);
  };

  // Fetch data on component mount
  useEffect(() => {
    refreshAllData();
  }, []);

  // Refetch scheduled notifications when pagination changes
  useEffect(() => {
    if (scheduledPagination.page > 1) {
      fetchScheduledNotifications();
    }
  }, [scheduledPagination.page]);

  // Refetch sent notifications when pagination changes
  useEffect(() => {
    if (sentPagination.page > 1) {
      fetchSentNotifications();
    }
  }, [sentPagination.page]);

  const handleUserSelection = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleGroupSelection = (groupId) => {
    setSelectedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleTemplateSelection = (templateId) => {
    setSelectedTemplates((prev) =>
      prev.includes(templateId)
        ? prev.filter((id) => id !== templateId)
        : [...prev, templateId]
    );
  };

  const handleScheduledSelection = (scheduledId) => {
    setSelectedScheduled((prev) =>
      prev.includes(scheduledId)
        ? prev.filter((id) => id !== scheduledId)
        : [...prev, scheduledId]
    );
  };

  const handleSendToUsersClick = async () => {
    if (selectedUsers.length === 0) {
      toast.warning("Please select users first");
      return;
    }
    // Clear any selected groups when sending to users
    setSelectedGroups([]);
    setShowSendNotification(true);
  };

  const handleSendToGroupClick = async () => {
    if (selectedGroups.length === 0) {
      toast.warning("Please select groups first");
      return;
    }
    // Clear any selected users when sending to groups
    setSelectedUsers([]);
    setShowSendNotification(true);
  };

  const handleTemplateAction = async (action, templateId) => {
    try {
      const template = templates.find((t) => t.id === templateId);
      if (!template) return;
      switch (action) {
        case "delete":
          const deleteResult =
            await NotificationApiService.deleteTemplate(templateId);
          if (deleteResult.success) {
            toast.success("Template deleted successfully");
            fetchTemplates();
          } else {
            toast.error(deleteResult.message || "Failed to delete template");
          }
          break;

        default:
          break;
      }
    } catch (error) {
      console.error("Error handling template action:", error);
      toast.error("Failed to perform action");
    }
  };

  const handleScheduledAction = async (action, scheduledId) => {
    try {
      const scheduled = scheduledNotifications.find(
        (n) => n.id === scheduledId
      );
      if (!scheduled) return;

      switch (action) {
        case "view":
          toast.info("View scheduled notification details");
          break;

        case "cancel":
          const result =
            await NotificationApiService.cancelScheduledNotification(
              scheduledId
            );
          if (result.success) {
            toast.success("Scheduled notification cancelled");
            fetchScheduledNotifications();
          } else {
            toast.error(result.message || "Failed to cancel notification");
          }
          break;

        default:
          break;
      }
    } catch (error) {
      console.error("Error handling scheduled notification action:", error);
      toast.error("Failed to perform action");
    }
  };

  const handleCreateGroup = async (groupData) => {
    try {
      console.log("Main component sending group data:", groupData);
      const result = await NotificationApiService.createGroup(groupData);

      if (result && result.success === true) {
        toast.success("Group created successfully");
        fetchGroups();
        setShowCreateGroup(false);
      } else {
        const errorMessage =
          result?.message || result?.error || "Failed to create group";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error creating group:", error);
    }
  };

  const handleCreateTemplate = async (templateData) => {
    try {
      const result = await NotificationApiService.createTemplate(templateData);

      // Debug: Log the full response
      console.log("Template creation response:", result);

      // Check if the response indicates success
      if (
        result &&
        (result.success === true ||
          result.statusCode === 200 ||
          result.statusCode === 201)
      ) {
        toast.success("Template created successfully");
        fetchTemplates();
        setShowCreateTemplate(false);
      } else {
        const errorMessage =
          result?.message || result?.error || "Failed to create template";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error creating template:", error);
    }
  };

  const handleSendToUsers = async (dto) => {
    try {
      const result = await NotificationApiService.sendToUsers(dto);
      console.log("Send to users response:", result);

      // Check if the response indicates success
      if (result.success) {
        toast.success("Notification sent successfully");
        fetchScheduledNotifications();
        fetchSentNotifications();
        setShowSendNotification(false);
      } else {
        const errorMessage =
          result?.message || result?.error || "Failed to send notification";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error sending notification:", error);
    } finally {
      setShowCreateScheduled(false);
    }
  };

  const handleSendToGroups = async (dto) => {
    try {
      const result = await NotificationApiService.sendToGroup(dto);
      if (result.success) {
        toast.success("Notification sent successfully");
        fetchSentNotifications();
        setShowSendNotification(false);
      } else {
        const errorMessage =
          result?.message || result?.error || "Failed to send notification";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error sending notification:", error);
    } finally {
      setShowCreateScheduled(false);
    }
  };

  const handleViewGroupDetails = (groupId) => {
    setSelectedGroupForDetails(groupId);
    setShowGroupDetails(true);
  };

  const handleRefresh = async () => {
    setUserSearchQuery("");
    setGroupSearchQuery("");
    setTemplateSearchQuery("");
    setScheduledSearchQuery("");
    setSelectedUsers([]);
    setSelectedGroups([]);
    setSelectedTemplates([]);
    setSelectedScheduled([]);
    await refreshAllData();
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: {
        bg: "bg-green-100 text-green-800",
        icon: <CheckCircle className="h-3 w-3" />,
      },
      pending: {
        bg: "bg-blue-100 text-blue-800",
        icon: <Clock className="h-3 w-3" />,
      },
      cancelled: {
        bg: "bg-gray-100 text-gray-800",
        icon: <AlertCircle className="h-3 w-3" />,
      },
      failed: {
        bg: "bg-red-100 text-red-800",
        icon: <AlertTriangle className="h-3 w-3" />,
      },
      active: {
        bg: "bg-green-100 text-green-800",
        icon: <CheckCircle className="h-3 w-3" />,
      },
      inactive: {
        bg: "bg-yellow-100 text-yellow-800",
        icon: <AlertTriangle className="h-3 w-3" />,
      },
    };

    const variant = variants[status] || {
      bg: "bg-gray-100 text-gray-800",
      icon: null,
    };

    return (
      <Badge
        className={`${variant.bg} capitalize flex items-center gap-1 text-xs`}
      >
        {variant.icon}
        {status}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const colors = {
      system: "bg-blue-100 text-blue-800",
      course: "bg-green-100 text-green-800",
      admin: "bg-purple-100 text-purple-800",
      welcome: "bg-yellow-100 text-yellow-800",
      reminder: "bg-orange-100 text-orange-800",
      alert: "bg-red-100 text-red-800",
      custom: "bg-pink-100 text-pink-800",
    };

    return (
      <Badge
        variant="outline"
        className={`capitalize text-xs ${colors[type] || "bg-gray-100 text-gray-800"}`}
      >
        {type}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Notification Management
          </h1>
          <p className="text-muted-foreground">
            Manage users, groups, templates, and scheduled notifications
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-5 gap-4">
        <Card className="border border-[#5D93A5] bg-white shadow-sm hover:shadow-md transition-all duration-200">
          <CardContent className="p-4">
            <div className="text-3xl font-bold text-[#5D93A5]">
              {stats.totalUsers}
            </div>
            <p className="text-md text-black font-semibold">Total Users</p>
          </CardContent>
        </Card>

        <Card className="border border-[#5D93A5] bg-white shadow-sm hover:shadow-md transition-all duration-200">
          <CardContent className="p-4">
            <div className="text-3xl font-bold text-[#5D93A5]">
              {stats.totalGroups}
            </div>
            <p className="text-md text-black font-semibold">Groups</p>
          </CardContent>
        </Card>

        <Card className="border border-[#5D93A5] bg-white shadow-sm hover:shadow-md transition-all duration-200">
          <CardContent className="p-4">
            <div className="text-3xl font-bold text-[#5D93A5]">
              {stats.totalTemplates}
            </div>
            <p className="text-md text-black font-semibold">Templates</p>
          </CardContent>
        </Card>

        <Card className="border border-[#5D93A5] bg-white shadow-sm hover:shadow-md transition-all duration-200">
          <CardContent className="p-4">
            <div className="text-3xl font-bold text-[#5D93A5]">
              {stats.totalScheduled}
            </div>
            <p className="text-md text-black font-semibold">Scheduled</p>
          </CardContent>
        </Card>

        <Card className="border border-[#5D93A5] bg-white shadow-sm hover:shadow-md transition-all duration-200">
          <CardContent className="p-4">
            <div className="text-3xl font-bold text-[#5D93A5]">
              {stats.totalSent}
            </div>
            <p className="text-md text-black font-semibold">Sent</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content - Three Panel Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Users and Groups */}
        <div className="space-y-6">
          {/* Users Section */}
          <Card>
            <CardHeader className="flex justify-between">
              <CardTitle className="text-lg">Users</CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={handleSendToUsersClick}
                  disabled={selectedUsers.length === 0}
                >
                  <Send className="mr-2 h-3 w-3" />
                  Send Notification
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="User Search"
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {isLoadingUsers ? (
                  <div className="flex items-center justify-center h-20">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center text-sm text-muted-foreground py-4">
                    No users found
                  </div>
                ) : (
                  users
                    .filter(
                      (user) =>
                        (user.name || "")
                          .toLowerCase()
                          .includes(userSearchQuery.toLowerCase()) ||
                        (user.indianCdc || "")
                          .toLowerCase()
                          .includes(userSearchQuery.toLowerCase()) ||
                        (user.email || "")
                          .toLowerCase()
                          .includes(userSearchQuery.toLowerCase())
                    )
                    .map((user) => (
                      <div
                        key={user.id}
                        className="flex items-start gap-4 p-4 border rounded-lg shadow-sm hover:shadow-md transition-all duration-200 bg-white border-[#5D93A5]"
                      >
                        {/* Checkbox */}
                        <Checkbox
                          checked={selectedUsers.includes(user.id)}
                          onCheckedChange={() => handleUserSelection(user.id)}
                          className="mt-1"
                        />

                        {/* User Info */}
                        <div className="flex-1 min-w-0">
                          {/* Name */}
                          <div className="font-semibold text-base truncate text-[#5D93A5]">
                            {user.name ||
                              `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                              "Unknown User"}
                          </div>

                          {/* Details */}
                          <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                            <div className="flex items-center flex-wrap gap-1 text-black">
                              <span className="font-medium">INDoS No:</span>{" "}
                              {user.indianCdc || "N/A"}
                            </div>
                            <div className="flex items-center gap-1 text-black">
                              <span className="font-medium">Age:</span>{" "}
                              {user.age || "N/A"}
                            </div>
                            <div className="flex items-center gap-1 text-black">
                              <span className="font-medium">Ship IMO:</span>{" "}
                              {user.currentShipImo || "N/A"}
                            </div>
                            <div className="flex items-center gap-1 text-black">
                              <span className="font-medium">Rank:</span>{" "}
                              {user.rankDesignation || "N/A"}
                            </div>
                            <div className="flex items-center gap-1 text-black">
                              <span className="font-medium">Gender:</span>{" "}
                              {user.gender || "N/A"}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Groups Section */}
          <Card>
            <CardHeader className="flex flex-wrap justify-between">
              <CardTitle className="text-lg">Group</CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowCreateGroup(true)}
                >
                  <Plus className="mr-2 h-3 w-3" />
                  Create Group
                </Button>
                <Button
                  size="sm"
                  onClick={handleSendToGroupClick}
                  disabled={selectedGroups.length === 0}
                >
                  <Send className="mr-2 h-3 w-3" />
                  Send Notification
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Group Search"
                  value={groupSearchQuery}
                  onChange={(e) => setGroupSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {isLoadingGroups ? (
                  <div className="flex items-center justify-center h-20">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                ) : groups.length === 0 ? (
                  <div className="text-center text-sm text-muted-foreground py-4">
                    No groups found
                  </div>
                ) : (
                  groups
                    .filter((group) =>
                      group.name
                        .toLowerCase()
                        .includes(groupSearchQuery.toLowerCase())
                    )
                    .map((group) => (
                      <div
                        key={group.id}
                        className="flex items-start gap-3 p-3 border rounded-lg hover:bg-muted/50"
                      >
                        <Checkbox
                          checked={selectedGroups.includes(group.id)}
                          onCheckedChange={() => handleGroupSelection(group.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">
                            {group.name}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            ({group.userCount || 0} Total member)
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewGroupDetails(group.id)}
                            className="h-6 px-2"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Middle Panel - Templates */}
        <Card className="max-h-[85vh] flex flex-col">
          <CardHeader className="pb-3 flex-shrink-0">
            <CardTitle className="text-lg">Templates</CardTitle>
            <Button size="sm" onClick={() => setShowCreateTemplate(true)}>
              <Plus className="mr-2 h-3 w-3" />
              Create Template
            </Button>
          </CardHeader>

          <CardContent className="flex flex-col flex-1 space-y-3 overflow-hidden">
            {/* Search */}
            <div className="relative flex-shrink-0">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search templates..."
                value={templateSearchQuery}
                onChange={(e) => setTemplateSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Templates list */}
            <div className="flex-1 overflow-y-auto space-y-3">
              {isLoadingTemplates ? (
                <div className="flex items-center justify-center h-20">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#5D93A5]"></div>
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center text-sm text-gray-500 py-4">
                  No templates found
                </div>
              ) : (
                templates
                  .filter(
                    (template) =>
                      template.name
                        .toLowerCase()
                        .includes(templateSearchQuery.toLowerCase()) ||
                      template.type
                        .toLowerCase()
                        .includes(templateSearchQuery.toLowerCase())
                  )
                  .map((template) => (
                    <div
                      key={template.id}
                      className="p-4 border border-[#5D93A5] rounded-lg bg-white hover:bg-[#5D93A5]/10 transition-colors duration-200"
                    >
                      {/* Header */}
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                        <div className="font-semibold text-sm text-[#5D93A5] break-words">
                          Template Name: {template.name}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-black hover:text-[#5D93A5]"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() =>
                                handleTemplateAction("delete", template.id)
                              }
                              className="text-red-600"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Details */}
                      <div className="text-xs text-black space-y-1">
                        <div className="flex flex-col sm:flex-row sm:gap-2">
                          <span className="font-medium">Type:</span>{" "}
                          {getTypeBadge(template.type)}
                        </div>
                        <div className="truncate sm:whitespace-normal sm:break-words">
                          <span className="font-medium">Template Body:</span>{" "}
                          {template.bodyTemplate}
                        </div>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Right Panel - Scheduled Notifications */}
        <Card className="max-h-[85vh] flex flex-col">
          {/* Header - stays fixed */}
          <CardHeader className="pb-3 flex-shrink-0">
            <div className="flex items-center flex-wrap justify-between">
              <div>
                <CardTitle className="text-lg">
                  Scheduled Notifications
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {scheduledPagination.total} total • Page{" "}
                  {scheduledPagination.page} of {scheduledPagination.totalPages}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setScheduledPagination((prev) => ({ ...prev, page: 1 }));
                    fetchScheduledNotifications();
                  }}
                  disabled={isLoadingScheduled}
                >
                  {isLoadingScheduled ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  ) : (
                    "Refresh"
                  )}
                </Button>
                <Button size="sm" onClick={() => setShowCreateScheduled(true)}>
                  <Plus className="mr-2 h-3 w-3" />
                  Create
                </Button>
              </div>
            </div>
          </CardHeader>

          {/* Content - fills remaining space */}
          <CardContent className="flex flex-col flex-1 space-y-3 overflow-hidden">
            {/* Search bar */}
            <div className="relative flex-shrink-0">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search scheduled notifications..."
                value={scheduledSearchQuery}
                onChange={(e) => setScheduledSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Scrollable list */}
            <div className="flex-1 overflow-y-auto space-y-2">
              {isLoadingScheduled ? (
                <div className="flex items-center justify-center h-20">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : scheduledNotifications.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-4">
                  No scheduled notifications found
                </div>
              ) : (
                scheduledNotifications
                  .filter(
                    (notification) =>
                      notification.subject
                        .toLowerCase()
                        .includes(scheduledSearchQuery.toLowerCase()) ||
                      notification.type
                        .toLowerCase()
                        .includes(scheduledSearchQuery.toLowerCase())
                  )
                  .map((notification) => (
                    <div
                      key={notification.id}
                      className="p-3 border rounded-lg hover:bg-muted/50"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="font-medium text-sm">
                          {notification.subject}
                        </div>
                        <div className="flex items-center gap-2">
                          {notification.createdAt && (
                            <div className="text-xs text-muted-foreground">
                              Created: {formatDate(notification.createdAt)}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1 mb-2">
                        <div className="truncate">{notification.body}</div>
                        <div>
                          Scheduled Time:{" "}
                          {formatDate(notification.scheduledFor)}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        {getStatusBadge(notification.status)}
                        {/* <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs h-6 px-2"
                          onClick={() =>
                            handleScheduledAction("view", notification.id)
                          }
                        >
                          View more
                        </Button> */}
                      </div>
                    </div>
                  ))
              )}
            </div>

            {/* Pagination - fixed at bottom of content */}
            {scheduledPagination.totalPages > 1 && (
              <div className="flex-shrink-0 flex items-center justify-between pt-3 border-t">
                <div className="text-sm text-muted-foreground">
                  Showing{" "}
                  {(scheduledPagination.page - 1) * scheduledPagination.limit +
                    1}{" "}
                  to{" "}
                  {Math.min(
                    scheduledPagination.page * scheduledPagination.limit,
                    scheduledPagination.total
                  )}{" "}
                  of {scheduledPagination.total} results
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (scheduledPagination.hasPrev) {
                        setScheduledPagination((prev) => ({
                          ...prev,
                          page: prev.page - 1,
                        }));
                      }
                    }}
                    disabled={!scheduledPagination.hasPrev}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground px-2">
                    {scheduledPagination.page}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (scheduledPagination.hasNext) {
                        setScheduledPagination((prev) => ({
                          ...prev,
                          page: prev.page + 1,
                        }));
                      }
                    }}
                    disabled={!scheduledPagination.hasNext}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sent Notifications Section */}
      <Card className="mt-6">
        <CardHeader className="pb-3">
          <div className="flex items-center flex-wrap justify-between">
            <div>
              <CardTitle className="text-lg">Sent Notifications</CardTitle>
              <p className="text-sm text-muted-foreground">
                {sentPagination.total} total • Page {sentPagination.page} of{" "}
                {sentPagination.totalPages}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchSentNotifications}
              disabled={isLoadingSent}
            >
              {isLoadingSent ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              ) : (
                "Refresh"
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search sent notifications..."
              value={sentSearchQuery}
              onChange={(e) => setSentSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto rounded-lg border border-border bg-background p-2">
            {isLoadingSent ? (
              <div className="flex items-center justify-center h-24">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : sentNotifications.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-8">
                No sent notifications found
              </div>
            ) : (
              sentNotifications
                .filter(
                  (notification) =>
                    notification.subject
                      ?.toLowerCase()
                      .includes(sentSearchQuery.toLowerCase()) ||
                    notification.body
                      ?.toLowerCase()
                      .includes(sentSearchQuery.toLowerCase())
                )
                .map((notification) => (
                  <div
                    key={notification.id}
                    className="rounded-xl border border-border bg-card/70 p-4 shadow-sm transition-colors hover:bg-muted/40 hover:shadow-md"
                  >
                    {/* Header Row */}
                    <div className="flex flex-wrap items-start justify-between gap-3 pb-2">
                      <div className="min-w-0 space-y-1">
                        <button
                          type="button"
                          onClick={() =>
                            window.open(`mailto:${notification.user.email}`, "_blank")
                          }
                          className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-[var(--secondary-font)] hover:bg-muted hover:text-foreground"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            className="h-3.5 w-3.5"
                            fill="currentColor"
                          >
                            <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 2v.511l-8 5.333-8-5.333V6Zm0 12H4V8.156l7.385 4.923a1 1 0 0 0 1.23 0L20 8.156Z" />
                          </svg>
                          {notification.user.email}
                        </button>
                        <span className="block truncate text-sm font-semibold text-foreground">
                          {notification.subject}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(
                          notification.sentAt || notification.createdAt
                        )}
                      </div>
                    </div>

                    {/* Body Text */}
                    <div className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                      {notification.body}
                    </div>

                    {/* Footer Row */}
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                      {getTypeBadge(notification.type)}
                      {notification.recipientCount && (
                        <span className="rounded-full bg-muted px-3 py-1 font-medium text-muted-foreground">
                          Sent to {notification.recipientCount} recipient
                          {notification.recipientCount > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                ))
            )}
          </div>

          {sentPagination.totalPages > 1 && (
            <div className="flex items-center justify-between pt-3 border-t">
              <div className="text-sm text-muted-foreground">
                Showing {(sentPagination.page - 1) * sentPagination.limit + 1}{" "}
                to{" "}
                {Math.min(
                  sentPagination.page * sentPagination.limit,
                  sentPagination.total
                )}{" "}
                of {sentPagination.total} results
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (sentPagination.hasPrev) {
                      setSentPagination((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }));
                    }
                  }}
                  disabled={!sentPagination.hasPrev}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground px-2">
                  {sentPagination.page}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (sentPagination.hasNext) {
                      setSentPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }));
                    }
                  }}
                  disabled={!sentPagination.hasNext}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <CreateGroupModal
        isOpen={showCreateGroup}
        onClose={() => setShowCreateGroup(false)}
        onCreateGroup={handleCreateGroup}
      />

      <CreateTemplateModal
        isOpen={showCreateTemplate}
        onClose={() => setShowCreateTemplate(false)}
        onCreateTemplate={handleCreateTemplate}
      />

      <CreateScheduledNotificationModal
        isOpen={showCreateScheduled}
        onClose={() => setShowCreateScheduled(false)}
        templates={templates}
        groups={groups}
        users={users}
        onSendToUsers={handleSendToUsers}
        onSendToGroups={handleSendToGroups}
      />

      <SendNotificationModal
        isOpen={showSendNotification}
        onClose={() => setShowSendNotification(false)}
        templates={templates}
        groups={groups}
        users={users}
        selectedUsers={selectedUsers}
        selectedGroups={selectedGroups}
        onSendToUsers={handleSendToUsers}
        onSendToGroups={handleSendToGroups}
      />

      <GroupDetailsModal
        isOpen={showGroupDetails}
        onClose={() => {
          setShowGroupDetails(false);
          setSelectedGroupForDetails(null);
        }}
        groupId={selectedGroupForDetails || ""}
      />
    </div>
  );
}
